import React, { FC, useCallback } from 'react';
import { ORDER_DETAILS_SORT_BY } from '../../shared/common/SortByDropDown';
import { CheckoutSummary } from '../checkout/CheckoutSummary';
import { CheckoutListHeader } from '../checkout/CheckoutListItem';
import Pagination from '../../shared/common/Pagination';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import DocumentTitle from '../../shared/components/DocumentTitle';
import FilterBar from '../../shared/common/FilterBar';
import { ContainerInner } from '../../shared/common/ContainerInner';
import { InvoiceOut } from '../../store/api';
import { downloadInvoice } from '../../api/invoice/downloadInvoice';
import { globalError } from '../../shared/notification/actions';
import { useOrderQuery } from './useOrderQuery';
import { useOrderProductsQuery } from './useOrderProductsQuery';
import { usePaginationAndSortParams } from '../../shared/hooks/usePaginationAndSortParams';
import { OrdersDetailsProductList } from './OrdersDetailsProductList';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useParams } from 'react-router';

const OrdersDetailsView: FC = () => {
    const { id: orderId = '' } = useParams<'id'>();
    const { t } = useTranslation();
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultSortBy: 'titleAZ',
        defaultPageSize: 50
    });
    const order = useOrderQuery(orderId);
    const orderProducts = useOrderProductsQuery(orderId, { page, pageSize, sortBy });

    const onDownloadInvoice = useCallback((invoice: InvoiceOut) => {
        downloadInvoice(invoice).catch(error => globalError(error));
    }, []);

    const isLoading = order.status === 'loading' || orderProducts.status === 'loading';

    return (
        <ContainerInner isLoading={isLoading}>
            {isLoading ? (
                <LoadingWrapper>
                    <LoadingOverlay loading={true} />
                </LoadingWrapper>
            ) : null}
            <DocumentTitle title={t('components.documentTitle.order', { orderId })}>
                <div className="checkout bw-wrap medium">
                    <div className="txt-upper">{t('shop.orders.orderSummary')}</div>
                    <div className={classNames('content-area-inner', { 'min-h-screen': isLoading })}>
                        <CheckoutSummary
                            {...order.data}
                            accounts={{}}
                            standingOrders={{} as any}
                            isOrderDetail={true}
                            readOnlyFields="all"
                            onDownloadInvoice={onDownloadInvoice}
                        />
                        <div className={classNames('checkout-pl', { 'min-h-screen': isLoading })}>
                            <FilterBar
                                sortBy={sortBy}
                                totalProducts={orderProducts.data.totalHits}
                                pageSize={pageSize}
                                sorting={ORDER_DETAILS_SORT_BY}
                                isFilterToggleReplaced={true}
                                leftColumnText={t('shop.orders.items')}
                                hideGroupBy={true}
                                hideSelectButtons={true}
                                hideSelectAll={true}
                                hideDisplayType={true}
                            />
                            <CheckoutListHeader />
                            <OrdersDetailsProductList
                                orderId={orderId}
                                lineItems={orderProducts.data.lineItems}
                                onDownloadInvoice={onDownloadInvoice}
                            />
                            <Pagination totalItems={orderProducts.data.totalHits} pageSize={pageSize} />
                        </div>
                    </div>
                </div>
            </DocumentTitle>
        </ContainerInner>
    );
};

export default OrdersDetailsView;
